import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { queryKeys } from '../../lib/queryClient';
import { useAuth } from '../../pages/Auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Types
export type SubscriptionTier = 'free' | 'pro' | 'lifetime';
export type ExportFormat = 'png' | 'svg' | 'pdf';

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  isPaid: boolean;
  diagrams: {
    current: number;
    max: number;
    remaining: number;
    canCreate: boolean;
  };
  exportFormats: ExportFormat[];
  subscriptionExpiresAt?: string | null;
  isCanceling?: boolean;
}

// Tier limits (mirrored from backend for instant checks)
const TIER_LIMITS = {
  free: {
    maxDiagrams: 10,
    exportFormats: ['png'] as ExportFormat[],
  },
  pro: {
    maxDiagrams: Infinity,
    exportFormats: ['png', 'svg'] as ExportFormat[],
  },
  lifetime: {
    maxDiagrams: Infinity,
    exportFormats: ['png', 'svg'] as ExportFormat[],
  },
} as const;

/**
 * Fetch subscription info from API
 */
async function fetchSubscriptionInfo(): Promise<SubscriptionInfo> {
  const response = await fetch(`${API_URL}/api/diagrams/subscription?_=${Date.now()}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch subscription info');
  }

  return response.json();
}

/**
 * Helper to get effective tier considering expiration
 */
function getEffectiveTier(
  rawTier: SubscriptionTier,
  subscriptionExpiresAt?: string | null
): SubscriptionTier {
  if (rawTier === 'pro' && subscriptionExpiresAt) {
    const expiresAt = new Date(subscriptionExpiresAt);
    if (expiresAt < new Date()) {
      return 'free';
    }
  }
  return rawTier;
}

/**
 * Query-based subscription hook with caching
 */
export function useSubscriptionQuery() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Get raw tier and expiry from user object for fallback
  const userData = user as { subscriptionTier?: string; subscriptionExpiresAt?: string } | null;
  const rawTier: SubscriptionTier = (userData?.subscriptionTier as SubscriptionTier) || 'free';
  const subscriptionExpiresAt = userData?.subscriptionExpiresAt || null;
  const effectiveTier = getEffectiveTier(rawTier, subscriptionExpiresAt);
  const limits = TIER_LIMITS[effectiveTier] || TIER_LIMITS.free;

  // Query for subscription info
  const {
    data: subscriptionInfo,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.subscription.info(),
    queryFn: fetchSubscriptionInfo,
    enabled: isAuthenticated,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Provide fallback data based on user object
    placeholderData: {
      tier: effectiveTier,
      isPaid: effectiveTier === 'pro' || effectiveTier === 'lifetime',
      diagrams: {
        current: 0,
        max: limits.maxDiagrams,
        remaining: limits.maxDiagrams,
        canCreate: true,
      },
      exportFormats: limits.exportFormats as ExportFormat[],
    },
  });

  // Use API data or fallback
  const tier = subscriptionInfo?.tier ?? effectiveTier;
  const isPaidUser = tier === 'pro' || tier === 'lifetime';

  // Check if subscription is canceling
  const isCanceling = rawTier === 'pro' && subscriptionExpiresAt
    ? new Date(subscriptionExpiresAt) > new Date()
    : false;

  // Check if user can export in a specific format
  const canExportFormat = useCallback(
    (format: ExportFormat): boolean => {
      if (isPaidUser) return true;
      return limits.exportFormats.includes(format);
    },
    [isPaidUser, limits.exportFormats]
  );

  // Get diagram usage string
  const diagramUsageString = subscriptionInfo
    ? subscriptionInfo.diagrams.max === Infinity
      ? `${subscriptionInfo.diagrams.current} diagrams`
      : `${subscriptionInfo.diagrams.current}/${subscriptionInfo.diagrams.max}`
    : '';

  // Refresh function
  const refresh = useCallback(() => {
    return refetch();
  }, [refetch]);

  // Invalidate subscription cache (call after diagram create/delete)
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.subscription.info() });
  }, [queryClient]);

  return {
    // Tier info
    tier,
    isPaidUser,
    isFreeTier: tier === 'free',
    isCanceling,
    subscriptionExpiresAt,

    // Detailed info (from API)
    subscriptionInfo,

    // Diagram limits
    canCreateDiagram: subscriptionInfo?.diagrams.canCreate ?? true,
    diagramCount: subscriptionInfo?.diagrams.current ?? 0,
    maxDiagrams: subscriptionInfo?.diagrams.max ?? limits.maxDiagrams,
    remainingDiagrams: subscriptionInfo?.diagrams.remaining ?? limits.maxDiagrams,
    diagramUsageString,

    // Export limits
    canExportFormat,
    allowedExportFormats: limits.exportFormats,

    // State
    isLoading,
    error: error?.message ?? null,

    // Actions
    refresh,
    invalidate,
  };
}

export default useSubscriptionQuery;
